import React from "react";
import { motion } from "framer-motion";
import { CheckIcon } from "@heroicons/react/24/solid";
import { PhotoIcon } from "@heroicons/react/24/outline";
import AutoFitText from "./AutoFitText";
import { useTheme } from "../contexts/ThemeContext";
import { formatCurrencyAdaptive } from "../utils/numberFormat";
import {
  DEFAULT_CALENDAR_CARD_APPEARANCE,
  getCalendarCardTradeStyles,
} from "../utils/calendarCardAppearance";
import { withAlpha } from "../utils/themes";
import useMediaQuery from "../utils/useMediaQuery";

const getIntegerDigits = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 1;
  const abs = Math.abs(numeric);
  if (abs < 1) return 1;
  return Math.floor(Math.log10(abs)) + 1;
};

export const getCalendarPnlFontBounds = ({ absPnl, isMonthView, compact, formattedLength = 0 }) => {
  const digits = getIntegerDigits(absPnl);
  let bounds;

  if (isMonthView) {
    if (digits >= 8) bounds = { maxPx: 18, minPx: 9 };
    else if (digits >= 6) bounds = { maxPx: 22, minPx: 10 };
    else if (digits >= 4) bounds = { maxPx: 26, minPx: 12 };
    else bounds = { maxPx: 28, minPx: 14 };
  } else if (compact) {
    if (digits >= 8) bounds = { maxPx: 14, minPx: 8 };
    else if (digits >= 6) bounds = { maxPx: 18, minPx: 9 };
    else if (digits >= 4) bounds = { maxPx: 20, minPx: 10 };
    else bounds = { maxPx: 22, minPx: 12 };
  } else {
    if (digits >= 8) bounds = { maxPx: 16, minPx: 10 };
    else if (digits >= 6) bounds = { maxPx: 20, minPx: 11 };
    else if (digits >= 4) bounds = { maxPx: 22, minPx: 12 };
    else bounds = { maxPx: 24, minPx: 14 };
  }

  // Whole-dollar values can stay slightly larger. Extra characters from cents,
  // commas, or units step the cap down before AutoFitText handles tight cards.
  const lengthPenalty = Math.min(3, Math.max(0, formattedLength - 4));
  return {
    ...bounds,
    maxPx: Math.max(bounds.minPx, bounds.maxPx - lengthPenalty),
  };
};

const getCalendarOutcome = (color) => {
  if (color === "blue") return "breakEven";
  if (color === "green") return "win";
  if (color === "red") return "loss";
  return "neutral";
};

const CenteredSignedValue = ({
  value,
  magnitude,
  showPositiveSign = true,
  testId,
}) => {
  const numeric = Number(value);
  const sign = numeric < 0 ? "-" : numeric > 0 && showPositiveSign ? "+" : "";

  return (
    <span
      data-testid={testId}
      aria-label={`${sign}${magnitude}`}
      className="inline-flex items-baseline justify-center tabular-nums"
    >
      <span aria-hidden="true">{sign}</span>
      <span>{magnitude}</span>
    </span>
  );
};

const DayCardTrigger = ({
  compact,
  onOpenDayCard,
  dayCardLabel,
  themeMeta,
}) => {
  if (!onOpenDayCard) return null;

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    event.stopPropagation();
    onOpenDayCard();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={dayCardLabel || "Open day score card"}
      title={dayCardLabel || "Open day score card"}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpenDayCard();
      }}
      onKeyDown={handleKeyDown}
      className={`absolute ${
        compact
          ? "bottom-1 right-1"
          : "bottom-1 right-1 sm:bottom-1.5 sm:right-1.5 md:bottom-2 md:right-2"
      } w-6 h-6 sm:w-7 sm:h-7 rounded-full border transition-all duration-150 flex items-center justify-center opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100`}
      style={{
        borderColor: themeMeta.tokens.border,
        backgroundColor: withAlpha(themeMeta.tokens.surface, 0.92),
        color: themeMeta.tokens.textMuted,
      }}
    >
      <PhotoIcon className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
    </div>
  );
};

const MonthHeader = ({ label, subLabel, themeMeta }) => (
  <div className="absolute top-1 left-1 right-12 sm:top-1.5 sm:left-1.5 sm:right-14 md:top-2 md:left-2 md:right-16">
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <div className="text-xs sm:text-sm font-medium" style={{ color: themeMeta.tokens.text }}>
        {label}
      </div>
      {subLabel ? (
        <div
          className="text-[10px] sm:text-xs font-medium"
          style={{ color: themeMeta.tokens.textMuted }}
        >
          {subLabel}
        </div>
      ) : null}
    </div>
  </div>
);

const CalendarBox = ({
  label,
  subLabel = "",
  onClick,
  color = "gray",
  pnl,
  rr = null,
  valueMode = "pnl",
  status,
  delay = 0,
  tradeCount = 0,
  percentage = 0,
  rulesFollowed = false,
  isMonthView = false,
  compact = false,
  entryCountOnly = 0,
  onOpenDayCard = null,
  dayCardLabel = "",
  appearance = DEFAULT_CALENDAR_CARD_APPEARANCE,
}) => {
  const { themeMeta } = useTheme();
  const isDarkMode = themeMeta.family === "dark";
  const displayValue = valueMode === "rr" ? rr : pnl;
  const absPnl = Math.abs(displayValue || 0);
  // Phones: day cells are ~48px wide, so show whole dollars (+777, -16, +1.2k) small enough
  // to sit under the date. Desktop keeps the exact amount.
  const isPhone = useMediaQuery("(max-width: 639px)");
  const phoneDayCell = isPhone && !isMonthView && !compact;
  const absPnlValue = Math.abs(Number(pnl) || 0);
  const primaryMagnitude = valueMode === "rr"
    ? (rr === null || rr === undefined ? "—" : `${Math.abs(Number(rr)).toFixed(phoneDayCell ? 1 : 2)}R`)
    : phoneDayCell
      ? (absPnlValue >= 1000 ? `${(absPnlValue / 1000).toFixed(1).replace(/\.0$/, "")}k` : String(Math.round(absPnlValue)))
      : formatCurrencyAdaptive(absPnlValue, { prefix: "" });
  const primarySign = Number(displayValue) < 0 ? "-" : Number(displayValue) > 0 ? "+" : "";
  const { maxPx: rawMaxPnlFont, minPx: minPnlFont } = getCalendarPnlFontBounds({
    absPnl,
    isMonthView,
    compact,
    formattedLength: `${primarySign}${primaryMagnitude}`.length,
  });
  const maxPnlFont = phoneDayCell ? Math.min(rawMaxPnlFont, 13) : rawMaxPnlFont;

  const outcome = getCalendarOutcome(color);
  const tradeStyles = getCalendarCardTradeStyles({
    appearance,
    outcome,
    isMonthView,
    isDarkMode,
    absPnl,
  });

  let borderColor = themeMeta.tokens.border;
  let heatmapStyle = {};

  if (status === "trades") {
    heatmapStyle = {
      backdropFilter: "blur(8px)",
      boxShadow: "inset 0 1px 1px rgba(255,255,255,0.3)",
    };

    if (tradeStyles.background) {
      heatmapStyle.background = tradeStyles.background;
    }

    if (tradeStyles.borderColor) {
      borderColor = tradeStyles.borderColor;
    }
  }

  const pnlTextColor =
    status === "trades"
      ? tradeStyles.pnlTextColor || themeMeta.tokens.text
      : themeMeta.tokens.textMuted;

  const percentageColor =
    status === "trades" && tradeCount > 0
      ? tradeStyles.percentageTextColor || themeMeta.tokens.textMuted
      : themeMeta.tokens.textMuted;

  const neutralCardStyle = {
    backgroundColor: themeMeta.tokens.bg,
    borderColor: themeMeta.tokens.border,
  };
  const cardSizeClass = compact
    ? "h-full max-h-full w-auto max-w-full aspect-square"
    : isMonthView
      ? "w-full min-h-[200px] sm:min-h-[240px] md:min-h-[280px]"
      : "w-full sm:w-[90%] md:w-[85%] aspect-square";

  if (status === "no-trades" && entryCountOnly > 0) {
    return (
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.3, delay }}
        className={`group rounded-card ${
          compact ? "p-1" : isMonthView ? "p-4 sm:p-5 md:p-7" : "p-2 sm:p-2.5 md:p-3"
        } ${cardSizeClass} mx-auto flex flex-col items-center justify-center relative transition-all duration-150 outline-none border`}
        style={neutralCardStyle}
        onClick={onClick}
      >
        {isMonthView ? (
          <MonthHeader label={label} subLabel={subLabel} themeMeta={themeMeta} />
        ) : (
          <div className="absolute top-1 left-1 sm:top-1.5 sm:left-1.5 md:top-2 md:left-2">
            <div
              className={compact ? "text-xs font-semibold" : "text-sm font-semibold"}
              style={{ color: themeMeta.tokens.text }}
            >
              {label}
            </div>
          </div>
        )}
        <div
          className={compact ? "text-xs font-medium" : "text-sm sm:text-base font-medium"}
          style={{ color: themeMeta.tokens.textMuted }}
        >
          {entryCountOnly} entry{entryCountOnly !== 1 ? "s" : ""}
        </div>
        {!isMonthView ? (
          <DayCardTrigger
            compact={compact}
            onOpenDayCard={onOpenDayCard}
            dayCardLabel={dayCardLabel}
            themeMeta={themeMeta}
          />
        ) : null}
      </motion.button>
    );
  }

  if (status === "no-trades") {
    if (isMonthView) {
      return (
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.3, delay }}
          className={`group rounded-card ${
            compact ? "p-1" : "p-4 sm:p-5 md:p-7"
          } ${cardSizeClass} mx-auto flex flex-col items-center justify-center relative transition-all duration-150 outline-none border border-dashed`}
          style={neutralCardStyle}
          onClick={onClick}
        >
          <MonthHeader label={label} subLabel={subLabel} themeMeta={themeMeta} />
          <span className="text-xs sm:text-sm italic" style={{ color: themeMeta.tokens.textMuted }}>
            -
          </span>
        </motion.button>
      );
    }

    // Empty days stay quieter (muted date, softer border) so trading days stand out.
    return (
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.3, delay }}
        data-day-state="empty"
        className={`group rounded-card ${
          compact ? "p-1" : "p-2 sm:p-2.5 md:p-3"
        } ${cardSizeClass} mx-auto flex flex-col items-center justify-center relative transition-all duration-150 outline-none border`}
        style={{
          ...neutralCardStyle,
          borderColor: withAlpha(themeMeta.tokens.border, 0.6),
        }}
        onClick={onClick}
      >
        <div
          data-testid="calendar-day-label"
          className={`absolute top-1 left-1 sm:top-1.5 sm:left-1.5 md:top-2 md:left-2 ${
            compact ? "text-xs" : "text-[10px] sm:text-xs md:text-sm"
          } font-semibold`}
          style={{ color: themeMeta.tokens.textMuted }}
        >
          {label}
        </div>
        <DayCardTrigger
          compact={compact}
          onOpenDayCard={onOpenDayCard}
          dayCardLabel={dayCardLabel}
          themeMeta={themeMeta}
        />
      </motion.button>
    );
  }

  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.3, delay }}
      className={`group rounded-card ${
        compact ? "p-1 pt-4" : isMonthView ? "p-4 sm:p-5 md:p-7" : "p-1 pt-4 sm:p-2.5 sm:pt-8 md:p-3 md:pt-9"
      } ${cardSizeClass} mx-auto flex flex-col items-center justify-center relative transition-all duration-150 outline-none`}
      style={{
        ...heatmapStyle,
        border: `1px solid ${borderColor}`,
      }}
      onClick={onClick}
    >
      {!isMonthView ? (
        <div
          className={`absolute top-1 left-1 sm:top-1.5 sm:left-1.5 md:top-2 md:left-2 ${
            compact ? "text-xs" : "text-sm"
          } font-semibold`}
          style={{ color: themeMeta.tokens.text }}
        >
          {label}
        </div>
      ) : null}

      {tradeCount > 0 ? (
        <div
          className={`absolute ${!isMonthView && !compact ? "hidden sm:block" : ""} ${
            compact
              ? "top-1 right-1 px-1 py-0.5 text-xs"
              : "top-1 right-1 sm:top-1.5 sm:right-1.5 md:top-2 md:right-2 px-1 py-0.5 sm:px-1 sm:py-0.5 md:px-1.5 md:py-0.5 text-xs sm:text-sm md:text-sm"
          } font-bold rounded-full shadow-lg`}
          style={{
            backgroundColor: withAlpha(themeMeta.tokens.accent, isDarkMode ? 0.42 : 0.22),
            color: themeMeta.tokens.text,
          }}
        >
          {tradeCount}
        </div>
      ) : null}

      {!isMonthView && rulesFollowed ? (
        <div
          className={
            compact
              ? "absolute bottom-1 left-1"
              : "absolute bottom-1 left-1 sm:bottom-1.5 sm:left-1.5 md:bottom-2 md:left-2"
          }
        >
          <CheckIcon
            className={compact ? "w-3 h-3" : "w-3 h-3 sm:w-4 sm:h-4 md:w-4 md:h-4"}
            style={{ color: themeMeta.tokens.success }}
          />
        </div>
      ) : null}

      {!isMonthView ? (
        <DayCardTrigger
          compact={compact}
          onOpenDayCard={onOpenDayCard}
          dayCardLabel={dayCardLabel}
          themeMeta={themeMeta}
        />
      ) : null}

      {isMonthView ? (
        <MonthHeader label={label} subLabel={subLabel} themeMeta={themeMeta} />
      ) : null}

      {status === "trades" ? (
        <AutoFitText
          minPx={minPnlFont}
          maxPx={maxPnlFont}
          className={`flex justify-center ${isMonthView ? "w-full px-4 sm:px-5 md:px-6" : ""}`}
          style={{ "--calendar-pnl-color": pnlTextColor }}
          textClassName="font-bold select-none font-mono [color:var(--calendar-pnl-color)]"
        >
          {valueMode === "rr" ? (
            rr === null || rr === undefined
              ? "—"
              : (
                <CenteredSignedValue
                  value={rr}
                  magnitude={primaryMagnitude}
                  testId="calendar-primary-value"
                />
              )
          ) : (
            <CenteredSignedValue
              value={pnl}
              magnitude={primaryMagnitude}
              testId="calendar-primary-value"
            />
          )}
        </AutoFitText>
      ) : null}

      {tradeCount > 0 ? (
        <div
          className={`${
            compact ? "text-xs" : "text-xs sm:text-sm md:text-sm"
          } ${!isMonthView && !compact ? "hidden sm:block" : ""} mt-0.5 sm:mt-1 font-mono`}
          style={{ color: percentageColor }}
        >
          {percentage >= 999999 ? (
            "New Account"
          ) : (
            <CenteredSignedValue
              value={percentage}
              magnitude={`${Math.abs(percentage).toFixed(1)}%`}
              testId="calendar-percentage-value"
            />
          )}
        </div>
      ) : null}
    </motion.button>
  );
};

export default CalendarBox;
